import { FUSO_EXIBICAO } from 'src/app/shared/tabela';
import { detalheProblema, ehCheckinJaRealizado } from './checkin-erros';

/**
 * Lógica pura do check-in retroativo (GUE-430): hora-de-parede ↔ instante e o
 * texto do erro. Sem Angular, para o teste não precisar de TestBed.
 *
 * O operador digita num `<input type="datetime-local">`, que devolve
 * hora-de-parede SEM fuso (`'2026-03-01T14:00'`). O contrato exige o instante
 * com offset explícito, convertido com o `timezone` DO EVENTO — a API não
 * infere fuso (ADR-0009). Nada aqui usa offset chumbado (`-03:00`): isso
 * quebra em horário de verão e em evento fora do Brasil.
 *
 * `spWallClockParaUtcIso` (shared/tabela) faz a mesma conversão, mas fixada em
 * America/Sao_Paulo — aqui a zona é parâmetro, que é a diferença toda.
 */

const FORMATADORES = new Map<string, Intl.DateTimeFormat>();

/** Formatador de partes na zona pedida; zona inválida cai no fuso de exibição. */
function formatador(timeZone: string): Intl.DateTimeFormat {
  const cache = FORMATADORES.get(timeZone);
  if (cache) return cache;
  const opcoes: Intl.DateTimeFormatOptions = {
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };
  let f: Intl.DateTimeFormat;
  try {
    f = new Intl.DateTimeFormat('en-US', { ...opcoes, timeZone });
  } catch {
    // Zona desconhecida do ICU (dado velho no banco, digitação do produtor):
    // melhor renderizar no fuso da casa do que estourar RangeError na cara do
    // operador. O backend valida a zona de verdade.
    f = new Intl.DateTimeFormat('en-US', { ...opcoes, timeZone: FUSO_EXIBICAO });
  }
  FORMATADORES.set(timeZone, f);
  return f;
}

interface Partes {
  ano: number;
  mes: number;
  dia: number;
  hora: number;
  minuto: number;
  segundo: number;
}

function partes(instante: Date, timeZone: string): Partes {
  const p = Object.fromEntries(
    formatador(timeZone)
      .formatToParts(instante)
      .map((parte) => [parte.type, parte.value]),
  );
  return {
    ano: Number(p['year']),
    mes: Number(p['month']),
    dia: Number(p['day']),
    // `hour12: false` devolve `24` para meia-noite em ICU antigo.
    hora: Number(p['hour']) % 24,
    minuto: Number(p['minute']),
    segundo: Number(p['second']),
  };
}

/** Deslocamento (ms) da zona em relação a UTC NAQUELE instante. */
function offsetMs(instante: Date, timeZone: string): number {
  const p = partes(instante, timeZone);
  const comoUtc = Date.UTC(p.ano, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
  // As partes têm resolução de segundo; truncar o instante mantém a subtração honesta.
  return comoUtc - Math.floor(instante.getTime() / 1000) * 1000;
}

function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Instante UTC → valor de `<input type="datetime-local">` na zona do evento
 * (`'YYYY-MM-DDTHH:mm'`). String vazia quando não dá para ler a data — é o que
 * o input aceita como "sem valor" em `min`/`max`/`value`.
 */
export function instanteParaHoraDeParede(
  iso: string | null | undefined,
  timeZone: string,
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = partes(d, timeZone);
  return `${p.ano}-${doisDigitos(p.mes)}-${doisDigitos(p.dia)}T${doisDigitos(p.hora)}:${doisDigitos(p.minuto)}`;
}

/**
 * Valor do `<input type="datetime-local">` (hora-de-parede na zona do evento)
 * → instante UTC ISO-8601 com `Z`, que é o que `CheckinRetroativoPayload`
 * pede. `null` quando o valor não é uma hora-de-parede completa.
 *
 * Duas passadas de propósito: a primeira usa o offset do instante errado (o
 * palpite), a segunda o do instante já corrigido. É o que acerta os dois dias
 * do ano em que a zona muda de offset.
 */
export function horaDeParedeParaInstante(
  valor: string | null | undefined,
  timeZone: string,
): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(valor ?? '');
  if (!m) return null;
  const alvo = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (Number.isNaN(alvo)) return null;
  const palpite = alvo - offsetMs(new Date(alvo), timeZone);
  return new Date(alvo - offsetMs(new Date(palpite), timeZone)).toISOString();
}

/**
 * Mensagem do erro de escrita, no vocabulário que a tela já usa
 * (`checkin-erros.ts`): quem manda é o `code`/`status`, e `detail` só entra
 * como texto — nunca como decisão.
 *
 * Vai INLINE no diálogo, não num `Swal` por cima: um segundo diálogo sobre o
 * modal aberto não recebe clique (o mesmo motivo que faz `executar()` fechar o
 * painel de detalhes antes de agir).
 */
export function mensagemErroRetroativo(err: unknown): string {
  if (ehCheckinJaRealizado(err)) {
    return detalheProblema(err, 'Este ingresso já teve check-in nesta janela.');
  }
  const status = (err as { status?: unknown } | null)?.status;
  if (status === 409) {
    return detalheProblema(err, 'Este ingresso já teve check-in nesta janela.');
  }
  if (status === 400) {
    return detalheProblema(
      err,
      'Horário inválido para esta janela. Confira o período e tente de novo.',
    );
  }
  if (status === 403) {
    return detalheProblema(
      err,
      'Sua permissão neste evento não inclui registrar check-in retroativo.',
    );
  }
  if (status === 404) {
    return detalheProblema(err, 'Janela ou ingresso não encontrado. Recarregue a lista.');
  }
  return detalheProblema(err, 'Não foi possível registrar o check-in retroativo.');
}
